import { AxiosInstance } from 'axios';
import { createAPIClient } from '@/utils/network-utils/createAPIClient';

import { InvalidStateError } from '@/utils/errors';
import { isNullish } from '@/utils/null-utils';
import type { ICoveoAnalyticsResponse } from '@/services/serverless/integrations/CoveoAPIService/CoveoAnalyticsService/ICoveoAnalyticsResponse';
import { UserType } from '@/services/models/User';
import Service from '../../../Service';
import ConfigurationService from '../../ConfigurationService';
import { EnvironmentService } from '../../EnvironmentService';
import I18NService from '../../I18NService';
import LoggerService from '../../LoggerService';

import CoveoAPIAnalyticsService from '../../../serverless/integrations/CoveoAPIService/CoveoAnalyticsService';

import type { IInteractionSearchDetails } from '../../UserInteractionService';

import type { IClickAnalyticsPayload } from './IClickAnalyticsPayload';
import type { ICoveoAnalyticsQuery } from './ICoveoAnalyticsQuery';
import type { ISearchAnalyticsPayload } from './ISearchAnalyticsPayload';

import CoveoAnalyticsServiceMock from './CoveoAnalyticsServiceMock';
import UserService from '../../UserService';
import type { IInteractionProductDetailsWithSearch } from '../../UserInteractionService/IInteractionDetails';

/**
 * The CoveoAnalyticsService handles taking client side click and search events
 * and sending them through to the api route to be handled by the CoveoAPIService.
 * @see https://docs.coveo.com/en/1373/build-a-search-ui/log-usage-analytics-events
 */
class CoveoAnalyticsService extends Service {
  private client: AxiosInstance;

  /** Initializes the service. */
  public constructor() {
    super();

    this.client = createAPIClient({ baseURL: '/api/coveo' });
  }

  /**
   * Executes a Coveo search query.
   * @param params - Search query parameters.
   * @returns The response in an {@link ICoveoSearchResponse} object.
   */
  private async query(
    params: ICoveoAnalyticsQuery
  ): Promise<ICoveoAnalyticsResponse> {
    if ((typeof window === "undefined")) {
      return CoveoAPIAnalyticsService.analyticsQuery(params);
    }

    // If not, forward the request to the server.
    const { data } = await this.client.post<ICoveoAnalyticsResponse>(
      'analytics',
      params
    );

    return data;
  }

  /**
   * Prepares the payload for the click event
   * and sends off the query to the api route.
   * This is usually made from the interaction hook after the
   * user clicks a product tile on a category or search.
   * @param interactionDetails - InteractionDetails with product data.
   * @returns The CoveoResponse or an Error.
   */
  public async clickEvent(
    interactionDetails: IInteractionProductDetailsWithSearch
  ): Promise<ICoveoAnalyticsResponse | Error> {
    const coveoConfig = ConfigurationService.getConfig('coveo');
    const { product, searchData } = interactionDetails;

    if (isNullish(searchData)) {
      throw new InvalidStateError(
        `CoveoAnalyticsService: searchData is not found in the interaction event and is needed to make a click analytics event to coveo.`
      );
    }

    // metadata.products is keyed by product id (UPC/sku from Coveo). IProduct.id is that same identifier.
    const productMetadata = searchData.products[product.id] ?? null;

    if (isNullish(productMetadata)) {
      throw new InvalidStateError(
        `CoveoAnalyticsService: Product metadata is not found for the product: ${product.id} and is needed to make a click analytics event to coveo.`
      );
    }

    try {
      const user = await UserService.getCurrentUser();
      const anonymous = user.type === UserType.Guest;

      const payload: IClickAnalyticsPayload = {
        actionCause: 'documentOpen',
        clientId: user.uuid,
        anonymous,
        documentPosition: searchData.documentPosition,
        documentTitle: productMetadata.title,
        documentUri: productMetadata.uri,
        documentUriHash: productMetadata.uriHash,
        documentUrl: productMetadata.uri,
        language: I18NService.currentLocale.language,
        originLevel1: coveoConfig.getSetting('searchHub').value,
        originLevel2: coveoConfig.getSetting('searchHub').value,
        searchQueryUid: searchData.searchQueryUid,
        sourceName: productMetadata.sourceName,
        userAgent: window.navigator.userAgent,
        customData: {
          contentIDKey: 'permanentid',
          contentIDValue: productMetadata.permanentId,
          name: productMetadata.title
        }
      };

      const queryParams: ICoveoAnalyticsQuery = { endpoint: '/click', payload };

      return await this.query(queryParams);
    } catch (error) {
      if (error instanceof InvalidStateError) {
        LoggerService.error(error.message);
      }

      return error as Error;
    }
  }

  /**
   * Prepares the search payload and sends it off for the api service.
   * Most of the payload is a aquired from the search response. The main call
   * is made from the q route on the server-side.
   * @param interactionDetails - The search details contains the search data
   * object assembled from the CoveoSearch response.
   * @returns The CoveoResponse or an Error.
   */
  public async searchEvent(
    interactionDetails: IInteractionSearchDetails
  ): Promise<ICoveoAnalyticsResponse | Error> {
    const coveoConfig = ConfigurationService.getConfig('coveo');

    const { searchData } = interactionDetails;

    if (isNullish(searchData)) {
      throw new InvalidStateError(
        `SearchData is not found and is needed to make a search analytics event`
      );
    }

    try {
      const user = await UserService.getCurrentUser();
      const anonymous = user.type === UserType.Guest;

      const payload: ISearchAnalyticsPayload = {
        actionCause: 'searchboxSubmit',
        clientId: user.uuid,
        anonymous,
        language: I18NService.currentLocale.language,
        originLevel1: coveoConfig.getSetting('searchHub').value,
        originLevel2: coveoConfig.getSetting('searchHub').value,
        searchQueryUid: searchData.searchQueryUid,
        userAgent: window.navigator.userAgent,
        queryText: searchData.queryText,
        responseTime: searchData.responseTime,
        numberOfResults: searchData.numberOfResults
      };

      const queryParams: ICoveoAnalyticsQuery = {
        endpoint: '/search',
        payload
      };

      return await this.query(queryParams);
    } catch (error) {
      if (error instanceof InvalidStateError) {
        LoggerService.error(error.message);
      }

      return error as Error;
    }
  }
}

export default CoveoAnalyticsService.withMock(
  new CoveoAnalyticsServiceMock(CoveoAnalyticsService)
) as unknown as CoveoAnalyticsService;

export type { CoveoAnalyticsService as CoveoAnalyticsServiceType };
